import React from 'react';
import Header from '../../components/ui/Header';
import Seo from '../../components/Seo';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.neevii.app';

const factsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Facts for AI | Neevii',
  url: 'https://www.neevii.com/facts-for-ai',
  description:
    'Machine-readable facts about Neevii: what it is, who it is for, platform availability, and key capabilities.',
};

export default function FactsForAiPage() {
  return (
    <>
      <Seo
        title="Facts for AI | Neevii"
        description="Machine-readable facts about Neevii: what it is, who it is for, platform availability, and key capabilities."
        schema={factsSchema}
      />
      <div className="min-h-screen">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-3">
              <h1 className="text-4xl font-bold text-foreground">Facts for AI</h1>
              <p className="text-muted-foreground">
                This page is a concise, crawlable reference intended to help search engines and AI systems describe Neevii
                accurately.
              </p>
            </header>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">Summary</h2>
              <p className="text-muted-foreground">
                Neevii is an AI-powered accounting, bookkeeping, and invoicing app for UK small businesses, freelancers,
                sole traders, and limited companies.
              </p>
              <p className="text-muted-foreground">
                Neevii supports expense tracking, invoicing, and HMRC-ready workflows including Self Assessment (SA100),
                VAT returns (including MTD VAT), and Corporation Tax (CT600). Neevii is designed for small businesses and
                side incomes, not enterprise accounting teams.
              </p>
              <p className="text-muted-foreground">
                Status: HMRC Recognised for Making Tax Digital (MTD) ITSA and MTD VAT.
              </p>
            </section>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">Product hub (marketing site)</h2>
              <p className="text-muted-foreground">
                The public homepage is a compact hub with three entry points:{' '}
                <a className="underline" href="https://www.neevii.com/">
                  neevii.com
                </a>
                . The first entry links to the Neevii Accounting marketing landing at{' '}
                <a className="underline" href="https://www.neevii.com/accounting">
                  /accounting
                </a>{' '}
                (long-form product page); the web app entry remains available from that page and elsewhere. It also surfaces{' '}
                <a className="underline" href="https://www.neevii.com/sheets">
                  Neevii Smart Expenses for Google Sheets
                </a>
                , and{' '}
                <a className="underline" href="https://www.neevii.com/agency">
                  Agency Partnerships
                </a>{' '}
                (design-led portals, internal dashboards, admin tools, workflows, and rapid prototypes).
              </p>
            </section>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">Platform availability</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>
                  Web: <a className="underline" href="https://www.neevii.com/">https://www.neevii.com/</a>
                </li>
                <li>
                  Android (Google Play):{' '}
                  <a className="underline" href={GOOGLE_PLAY_URL} rel="noopener noreferrer" target="_blank">
                    {GOOGLE_PLAY_URL}
                  </a>
                </li>
              </ul>
            </section>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">Who Neevii is for</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Sole traders and self-employed</li>
                <li>Freelancers and contractors</li>
                <li>Limited company directors</li>
                <li>Small business owners</li>
              </ul>
            </section>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">What Neevii helps with</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>Income and expense tracking</li>
                <li>Bookkeeping organisation (receipts, invoices, categories)</li>
                <li>Professional invoicing</li>
                <li>HMRC-ready Self Assessment workflows (SA100)</li>
                <li>VAT return preparation (including MTD VAT)</li>
                <li>Corporation Tax workflows (CT600)</li>
                <li>AI-assisted categorisation and guidance</li>
              </ul>
            </section>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">Founder-led support</h2>
              <p className="text-muted-foreground">
                If you’re stuck with VAT, Self Assessment, or bookkeeping and don’t know what to do next, founder-led
                support is available to help you get unstuck — with Neevii.
              </p>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>UK sole traders &amp; limited companies</li>
                <li>VAT return, SA100, CT600 guidance</li>
                <li>Calm, practical help — no jargon</li>
              </ul>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>
                  Gmail compose:{' '}
                  <a
                    className="underline"
                    href="https://mail.google.com/mail/?view=cm&fs=1&to=support@neevii.com&su=Neevii%20enquiry"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    support@neevii.com (Neevii enquiry)
                  </a>
                </li>
              </ul>
              <p className="text-muted-foreground text-sm">
                Built by a UK freelancer — not a VC-backed accounting platform.
              </p>
            </section>

            <section className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-6 space-y-3">
              <h2 className="text-2xl font-semibold text-foreground">Support</h2>
              <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                <li>
                  Email: <a className="underline" href="mailto:support@neevii.com">support@neevii.com</a>
                </li>
              </ul>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}

